import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { cityImage, INDIA_HERO } from "@/lib/cityImages";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Building2, Loader2, ArrowRight } from "lucide-react";

function useTitle(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag ? tag.getAttribute("content") : null;
    if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
    if (description) tag.setAttribute("content", description);
    return () => { document.title = prev; if (prevDesc != null) tag.setAttribute("content", prevDesc); };
  }, [title, description]);
}

export default function Cities() {
  const [cities, setCities] = useState(null);
  const [sort, setSort] = useState("count");
  useTitle("Medical Tourism Cities in India | SarvaSeva",
    "Explore accredited hospitals across 23 Indian cities. Compare specialties, savings and travel routes for medical tourism with SarvaSeva.");

  useEffect(() => {
    api.get("/v1/cities").then((r) => setCities(r.data.cities)).catch(() => setCities([]));
  }, []);

  const sorted = useMemo(() => {
    const list = [...(cities || [])];
    if (sort === "alpha") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => b.provider_count - a.provider_count || a.name.localeCompare(b.name));
    return list;
  }, [cities, sort]);

  return (
    <div className="bg-[#F7F9FC] min-h-screen" data-testid="cities-page">
      <section className="relative text-white overflow-hidden">
        <img src={INDIA_HERO} alt="India" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D5C75]/95 via-[#0D5C75]/80 to-[#2A9D8F]/60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Explore Medical Tourism Across India</h1>
          <p className="mt-4 text-white/90 max-w-2xl text-lg">Discover accredited hospitals city by city — compare specialties, savings, and travel routes across {cities?.length || 23} destinations.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground" data-testid="cities-count">
            {sorted.length} cities with partner hospitals
          </p>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[200px] h-9 rounded-full" data-testid="cities-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="count">Most Hospitals</SelectItem>
              <SelectItem value="alpha">Alphabetical (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!cities ? (
          <div className="py-20 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 items-stretch">
            {sorted.map((c, i) => (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
                <Link to={`/cities/${c.slug}`} data-testid={`city-card-${c.slug}`}
                  className="group card-modern tricolor-top overflow-hidden flex flex-col h-full">
                  <div className="relative h-44 overflow-hidden">
                    <img src={cityImage(c.name)} alt={c.name} loading="lazy"
                      className="mv-zoom-img absolute inset-0 h-full w-full object-cover bg-[#E4E7EC]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-heading font-extrabold text-2xl text-white leading-none drop-shadow">{c.name}</h3>
                      <p className="text-xs text-white/85 mt-1 flex items-center gap-1"><MapPin size={12} /> {c.state}</p>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0D5C75]">
                      <Building2 size={15} /> {c.provider_count} Partner Hospital{c.provider_count === 1 ? "" : "s"}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {c.top_specialties.map((s) => (
                        <Badge key={s} variant="outline" className="border-[#0D5C75]/25 bg-[#E9F5F9] text-[#0D5C75] text-xs">{s}</Badge>
                      ))}
                    </div>
                    <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D97706] group-hover:gap-2.5 transition-all">
                      Explore {c.name} <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
